import { DeploymentRecord } from '../types/DeploymentRecord';

export interface NeedsAttentionConfig {
  maxAgeHours: number;              // Default: 168 (7 days)
  productionEnvsOnly: boolean;      // Default: true
  productionEnvNames: string[];     // Default: ['prod', 'production']
  latestVersionOnly: boolean;       // Default: true
}

export const DEFAULT_CONFIG: NeedsAttentionConfig = {
  maxAgeHours: 168,
  productionEnvsOnly: false,
  productionEnvNames: ['prod', 'production'],
  latestVersionOnly: true,
};

/**
 * Check if a record's timestamp is within the allowed time limit
 */
export const isWithinTimeLimit = (record: DeploymentRecord, maxAgeHours: number): boolean => {
  if (maxAgeHours === 0) return true;
  const recordTime = new Date(record.timestamp).getTime();
  const cutoff = Date.now() - (maxAgeHours * 60 * 60 * 1000);
  return recordTime >= cutoff;
};

/**
 * Check if an environment name matches production environment patterns
 */
export const isProductionEnvironment = (envName: string, prodNames: string[]): boolean => {
  const normalized = envName.toLowerCase().trim();
  return prodNames.some(p =>
    normalized === p.toLowerCase() || normalized.includes(p.toLowerCase())
  );
};

/**
 * Parse a semver-like version string
 * Returns null if not parseable as semver
 */
const parseSemver = (version: string): { major: number; minor: number; patch: number } | null => {
  // Remove leading 'v' if present
  const cleaned = version.replace(/^v/i, '');

  // Try to match standard semver pattern (major.minor.patch)
  const match = cleaned.match(/^(\d+)\.(\d+)\.(\d+)/);
  if (match) {
    return {
      major: parseInt(match[1], 10),
      minor: parseInt(match[2], 10),
      patch: parseInt(match[3], 10),
    };
  }

  return null;
};

/**
 * Compare two semver objects
 * Returns: positive if a > b, negative if a < b, 0 if equal
 */
const semverCompare = (
  a: { major: number; minor: number; patch: number },
  b: { major: number; minor: number; patch: number }
): number => {
  if (a.major !== b.major) return a.major - b.major;
  if (a.minor !== b.minor) return a.minor - b.minor;
  return a.patch - b.patch;
};

/**
 * Compare two version strings
 * Returns: positive if a > b, negative if a < b, 0 if can't determine
 */
export const compareVersions = (a: string, b: string): number => {
  const semverA = parseSemver(a);
  const semverB = parseSemver(b);

  if (semverA && semverB) {
    return semverCompare(semverA, semverB);
  }

  // Fallback: can't determine version order
  return 0;
};

/**
 * Create a unique key for a service (combination of app + serviceName)
 */
const getServiceKey = (record: DeploymentRecord): string => {
  return `${record.app || ''}:${record.serviceName || ''}`;
};

/**
 * Get the latest version for each service from a set of records
 * Returns a map of serviceKey -> { version, timestamp }
 */
export const getLatestVersionPerService = (
  records: DeploymentRecord[]
): Map<string, { version: string; timestamp: string }> => {
  const latestVersions = new Map<string, { version: string; timestamp: string }>();

  records.forEach(record => {
    const serviceKey = getServiceKey(record);
    const existing = latestVersions.get(serviceKey);

    if (!existing) {
      latestVersions.set(serviceKey, {
        version: record.version,
        timestamp: record.timestamp
      });
      return;
    }

    // Compare versions
    const versionComparison = compareVersions(record.version, existing.version);

    if (versionComparison > 0) {
      // This version is newer by semver
      latestVersions.set(serviceKey, {
        version: record.version,
        timestamp: record.timestamp
      });
    } else if (versionComparison === 0) {
      // Can't compare by semver, use timestamp
      const recordTime = new Date(record.timestamp).getTime();
      const existingTime = new Date(existing.timestamp).getTime();

      if (recordTime > existingTime) {
        latestVersions.set(serviceKey, {
          version: record.version,
          timestamp: record.timestamp
        });
      }
    }
    // If versionComparison < 0, existing is newer, do nothing
  });

  return latestVersions;
};

/**
 * Check if a record represents the latest version for its service
 */
export const isLatestVersion = (
  record: DeploymentRecord,
  latestVersionsMap: Map<string, { version: string; timestamp: string }>
): boolean => {
  const serviceKey = getServiceKey(record);
  const latest = latestVersionsMap.get(serviceKey);

  if (!latest) return true; // No data, assume it's latest

  // Check if this record's version matches the latest
  return record.version === latest.version;
};

/**
 * Check if the latest version for a service has succeeded
 * Used to hide failures when a newer version has succeeded
 */
const hasLatestVersionSucceeded = (
  serviceKey: string,
  latestVersionsMap: Map<string, { version: string; timestamp: string }>,
  records: DeploymentRecord[]
): boolean => {
  const latest = latestVersionsMap.get(serviceKey);
  if (!latest) return false;

  // Find records for this service with the latest version
  const latestVersionRecords = records.filter(r =>
    getServiceKey(r) === serviceKey && r.version === latest.version
  );

  // Check if any of them succeeded
  return latestVersionRecords.some(r => r.status?.toLowerCase() === 'success');
};

/**
 * Main entry point: determine if any records should appear in "Needs Attention"
 * based on the provided configuration
 */
export function shouldShowInNeedsAttention(
  records: DeploymentRecord[],
  config: NeedsAttentionConfig
): boolean {
  let filtered = records;

  // 1. Environment filter - only include production environments
  if (config.productionEnvsOnly) {
    filtered = filtered.filter(r =>
      isProductionEnvironment(r.envName, config.productionEnvNames)
    );
  }

  // 2. Time filter - only include records within the time limit
  filtered = filtered.filter(r => isWithinTimeLimit(r, config.maxAgeHours));

  // 3. Get failed records
  const failed = filtered.filter(r => r.status?.toLowerCase() === 'failed');
  if (failed.length === 0) return false;

  // 4. Latest version filter
  if (config.latestVersionOnly) {
    const latestVersions = getLatestVersionPerService(filtered);

    // Check if any failed record is for the latest version
    // AND that latest version hasn't subsequently succeeded.
    // When a failure is hidden, log a traceable debug line so support can
    // answer "where did my failure go?" without guessing.
    return failed.some(r => {
      const serviceKey = getServiceKey(r);
      if (!isLatestVersion(r, latestVersions)) {
        if (typeof console !== 'undefined' && console.debug) {
          // eslint-disable-next-line no-console
          console.debug(
            '[needsAttention] hidden: failure is not latest version',
            { service: serviceKey, version: r.version, deployment_id: r.deployment_id }
          );
        }
        return false;
      }
      if (hasLatestVersionSucceeded(serviceKey, latestVersions, filtered)) {
        if (typeof console !== 'undefined' && console.debug) {
          // eslint-disable-next-line no-console
          console.debug(
            '[needsAttention] hidden: latest version subsequently succeeded',
            { service: serviceKey, version: r.version, deployment_id: r.deployment_id }
          );
        }
        return false;
      }
      return true;
    });
  }

  return true;
}
